import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock,
  faCreditCard,
  faLink,
  faMoon,
  faPlay,
  faRightFromBracket,
  faSun,
  faTableCells,
  faUserGear,
} from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../context/AppContext';
import { stripeBillingPortal } from '../services';
import { isTestEnvironment } from '../utils/isTestEnvironment';
import { BatchRange } from './Modals/BatchRange';
import { BatchStatic } from './Modals/BatchStatic';
import { BatchXero } from './Modals/BatchXero';
import darkLogo from '../assets/dark-logo.png';
import lightLogo from '../assets/light-logo.png';

export default function Navbar({ darkMode, onToggleDark }) {
  const navigate = useNavigate();
  const { base_url, token, company, clearSession, showStatus } = useAppContext();
  const [billingLoading, setBillingLoading] = useState(false);
  const [showBatchRangeModal, setShowBatchRangeModal] = useState(false);
  const [showBatchStaticModal, setShowBatchStaticModal] = useState(false);
  const [showBatchXeroModal, setShowBatchXeroModal] = useState(false);

  const canUseBatchStatic = [6].includes(company?.company_id);
  const canUseBatchRange = [7].includes(company?.company_id);
  const canUseBatchXero = [303, 8].includes(company?.company_id);
  const canUseBatch = canUseBatchStatic || canUseBatchRange || canUseBatchXero;
  const canUseQueue = [8, 303].includes(company?.company_id) || isTestEnvironment();

  const handleLogout = () => {
    clearSession();
    navigate('/login');
  };

  const handleBilling = async () => {
    if (billingLoading) return;
    if (!token) {
      showStatus('error', 'Please sign in again.');
      navigate('/login');
      return;
    }

    setBillingLoading(true);
    try {
      const url = await stripeBillingPortal(base_url, token);
      window.location.assign(url);
    } catch {
      showStatus('error', 'Failed to open billing.');
    } finally {
      setBillingLoading(false);
    }
  };

  const openBatchModal = () => {
    if (canUseBatchStatic) {
      setShowBatchStaticModal(true);
    } else if (canUseBatchRange) {
      setShowBatchRangeModal(true);
    } else if (canUseBatchXero) {
      setShowBatchXeroModal(true);
    }
  };

  return (
    <>
      <nav className="navbar">
        <NavLink to="/" className="navbar-brand">
          <div className="navbar-logo">
            <img
              src={darkMode ? darkLogo : lightLogo}
              alt=""
              width={28}
              height={28}
              decoding="async"
            />
          </div>
          <span className="navbar-name">Slipstream</span>
        </NavLink>

        <div className="navbar-links">
          {canUseBatch && (
            <button
              type="button"
              className="navbar-link"
              onClick={openBatchModal}
            >
              <FontAwesomeIcon className="navbar-link-icon" icon={faPlay} aria-hidden="true" />
              <span>Run Batch</span>
            </button>
          )}
          {canUseBatch && <div className="navbar-link-divider" aria-hidden="true" />}
          <NavLink
            to="/"
            end
            className={({ isActive }) => `navbar-link${isActive ? ' active' : ''}`}
          >
            <FontAwesomeIcon className="navbar-link-icon" icon={faTableCells} aria-hidden="true" />
            <span>Dashboard</span>
          </NavLink>
          {canUseQueue && (
            <NavLink
              to="/queue"
              className={({ isActive }) => `navbar-link${isActive ? ' active' : ''}`}
            >
              <FontAwesomeIcon className="navbar-link-icon" icon={faClock} aria-hidden="true" />
              <span>Queue</span>
            </NavLink>
          )}
          <NavLink
            to="/settings"
            className={({ isActive }) => `navbar-link${isActive ? ' active' : ''}`}
          >
            <FontAwesomeIcon className="navbar-link-icon" icon={faLink} aria-hidden="true" />
            <span>My Integration</span>
          </NavLink>
          <NavLink
            to="/profile"
            className={({ isActive }) => `navbar-link${isActive ? ' active' : ''}`}
          >
            <FontAwesomeIcon className="navbar-link-icon" icon={faUserGear} aria-hidden="true" />
            <span>Account Settings</span>
          </NavLink>
          <button
            type="button"
            className="navbar-link"
            onClick={handleBilling}
            disabled={billingLoading}
          >
            <FontAwesomeIcon className="navbar-link-icon" icon={faCreditCard} aria-hidden="true" />
            <span>{billingLoading ? 'Opening Billing...' : 'Billing'}</span>
          </button>
        </div>

        <div className="navbar-bottom">
          <button
            type="button"
            className="navbar-logout"
            onClick={handleLogout}
            aria-label="Sign out"
          >
            <FontAwesomeIcon icon={faRightFromBracket} aria-hidden="true" style={{ fontSize: '15px' }} />
            <span>Sign out</span>
          </button>
          <button className="theme-toggle" onClick={onToggleDark} aria-label="Toggle dark mode">
            {darkMode ? (
              <FontAwesomeIcon icon={faSun} aria-hidden="true" style={{ fontSize: '15px' }} />
            ) : (
              <FontAwesomeIcon icon={faMoon} aria-hidden="true" style={{ fontSize: '15px' }} />
            )}
            <span>{darkMode ? 'Light mode' : 'Dark mode'}</span>
          </button>
        </div>
      </nav>

      {canUseBatchRange && (
        <BatchRange isOpen={showBatchRangeModal} onClose={() => setShowBatchRangeModal(false)} />
      )}
      {canUseBatchStatic && (
        <BatchStatic isOpen={showBatchStaticModal} onClose={() => setShowBatchStaticModal(false)} />
      )}
      {canUseBatchXero && (
        <BatchXero isOpen={showBatchXeroModal} onClose={() => setShowBatchXeroModal(false)} />
      )}
    </>
  );
}
